/**
 * Activity Log Service
 * Handles logging and fetching activity for assignments, customers, and subcontractors
 *
 * All changes are always logged - no filtering/settings needed.
 */

import { supabase } from '../lib/supabase'

export interface ActivityLogEntry {
  id: string
  user_name: string | null
  action_type: string
  field_name: string | null
  old_value: string | null
  new_value: string | null
  description: string | null
  created_at: string
}

// ============================================
// Assignment Activity Log Functions
// ============================================

/**
 * Log an assignment change
 */
export async function logAssignmentChange(
  assignmentId: string,
  actionType: string,
  options?: {
    fieldName?: string
    oldValue?: string
    newValue?: string
    description?: string
  }
): Promise<void> {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    // Get user profile for name
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('full_name')
      .eq('user_id', user.id)
      .single()

    // Insert the log entry
    const { error } = await supabase
      .from('assignment_activity_log')
      .insert({
        assignment_id: assignmentId,
        user_id: user.id,
        user_name: profile?.full_name || user.email || 'Okänd användare',
        action_type: actionType,
        field_name: options?.fieldName,
        old_value: options?.oldValue,
        new_value: options?.newValue,
        description: options?.description
      })

    if (error) {
      console.error('Error logging activity:', error)
    }
  } catch (error) {
    console.error('Error in logAssignmentChange:', error)
  }
}

/**
 * Get activity log for an assignment
 */
export async function getAssignmentActivity(assignmentId: string): Promise<ActivityLogEntry[]> {
  const { data, error } = await supabase
    .from('assignment_activity_log')
    .select('*')
    .eq('assignment_id', assignmentId)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching activity log:', error)
    return []
  }

  return data || []
}

/**
 * Get human-readable action description
 */
export function getActionDescription(entry: ActivityLogEntry): string {
  const actionDescriptions: Record<string, string> = {
    // Original action types
    'created': 'skapade uppdraget',
    'status_changed': 'ändrade status',
    'date_changed': 'ändrade datum/tid',
    'subcontractor_changed': 'ändrade trädgårdsmästare',
    'customer_changed': 'ändrade kund',
    'articles_changed': 'ändrade artiklar',
    'field_changed': 'uppdaterade',
    'report_submitted': 'skickade in rapport',
    'report_reopened': 'öppnade rapport för redigering',

    // V2 Status System - Customer status
    'customer_comm_status_changed': 'ändrade kundkommunikationsstatus',
    'customer_invoice_status_changed': 'ändrade kundfaktureringsstatus',
    'customer_informed': 'informerade kunden',
    'customer_confirmed': 'kunden bekräftade',
    'customer_cancelled': 'kunden avbokade',
    'customer_invoiced': 'fakturerade kunden',

    // V2 Status System - TG status
    'tg_status_changed': 'ändrade trädgårdsmästarstatus',
    'tg_invoice_status_changed': 'ändrade trädgårdsmästarfakturastatus',
    'tg_assigned': 'tilldelade trädgårdsmästare',
    'tg_confirmed': 'bekräftade trädgårdsmästartilldelning',
    'tg_declined': 'trädgårdsmästare avsade sig',
    'tg_invoice_received': 'registrerade trädgårdsmästarfaktura',

    // V2 Status System - Rebooking
    'date_changed_with_rebooking': 'ombokade uppdraget',
    'planning_confirmed': 'bekräftade planeringen',

    // Attestation
    'report_attested': 'attesterade rapport',
    'report_rejected': 'begärde komplettering',
    'all_reports_attested': 'attesterade alla rapporter'
  }

  if (entry.description) return entry.description

  const baseDescription = actionDescriptions[entry.action_type] || 'gjorde en ändring'

  if (entry.field_name) {
    const fieldNames: Record<string, string> = {
      // Original fields
      'title': 'titel',
      'description': 'beskrivning',
      'start_date': 'startdatum',
      'start_time': 'starttid',
      'end_time': 'sluttid',
      'status': 'status',
      'location': 'plats',
      'estimated_hours': 'beräknade timmar',
      'recurring_type': 'återkommande typ',

      // V2 Customer fields
      'customer_comm_status': 'kundkommunikationsstatus',
      'customer_invoice_status': 'kundfaktureringsstatus',
      'customer_informed_at': 'informerad datum',
      'customer_informed_method': 'informerad via',
      'customer_confirmed_at': 'bekräftad datum',
      'customer_confirmed_method': 'bekräftad via',
      'customer_cancelled_at': 'avbokad datum',
      'customer_cancelled_reason': 'avbokningsorsak',
      'customer_invoiced_at': 'fakturerad datum',

      // V2 TG fields
      'tg_status': 'trädgårdsmästarstatus',
      'tg_invoice_status': 'trädgårdsmästarfakturastatus',
      'tg_invoice_received_at': 'faktura mottagen datum',
      'declined_at': 'avsagd datum',
      'declined_reason': 'avsägningsorsak'
    }
    const fieldLabel = fieldNames[entry.field_name] || entry.field_name
    return `${baseDescription} ${fieldLabel}`
  }

  return baseDescription
}

/**
 * Get status label in Swedish
 */
export function getStatusLabel(status: string): string {
  const statusLabels: Record<string, string> = {
    // Legacy assignment statuses (for backwards compatibility)
    'ej_tilldelad': 'Ej tilldelad',
    'planerad_ej_tilldelad': 'Planerad – Ej tilldelad',
    'tilldelad_ej_planerad': 'Tilldelad – Ej planerad',
    'preliminar_planering': 'Preliminär planering',
    'genomforande_rapportering': 'Genomförande & rapportering',
    'attest': 'Attest',
    'komplettering_rapportering': 'Komplettering av rapportering',
    'avbrutten': 'Avbruten',

    // V2 Assignment statuses
    'ohanterad': 'Ohanterad',
    'planering': 'Planering',
    'preliminar': 'Preliminär',
    'planerad': 'Planerad',
    'pagaende': 'Pågående',
    'granskning': 'Granskning',
    'attesterad': 'Attesterad',
    'slutford': 'Slutförd',
    'avbruten': 'Avbruten',

    // V2 TG work statuses
    'preliminar_tilldelad': 'Preliminärt tilldelad',
    'tilldelad': 'Tilldelad',
    'ej_rapporterad': 'Ej rapporterad',
    'rapporterad': 'Rapporterad',
    'komplettering': 'Komplettering',
    'godkand': 'Godkänd',
    'avsagt': 'Avsagt',

    // V2 TG invoice statuses
    'ej_fakturerad': 'Ej fakturerad',
    'faktura_mottagen': 'Faktura mottagen',

    // V2 Customer communication statuses
    'ej_kontaktad': 'Ej kontaktad',
    'informerad': 'Informerad',
    'bekraftad': 'Bekräftad',
    'avbokad': 'Avbokad',

    // V2 Customer invoice statuses
    'fakturerad': 'Fakturerad'
  }
  return statusLabels[status] || status
}

// ============================================
// Customer Activity Log Functions
// ============================================

/**
 * Log a customer change
 */
export async function logCustomerChange(
  customerId: string,
  actionType: string,
  options?: {
    fieldName?: string
    oldValue?: string
    newValue?: string
    description?: string
  }
): Promise<void> {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    // Get user profile for name
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('full_name')
      .eq('user_id', user.id)
      .single()

    // Insert the log entry
    const { error } = await supabase
      .from('customer_activity_log')
      .insert({
        customer_id: customerId,
        user_id: user.id,
        user_name: profile?.full_name || user.email || 'Okänd användare',
        action_type: actionType,
        field_name: options?.fieldName,
        old_value: options?.oldValue,
        new_value: options?.newValue,
        description: options?.description
      })

    if (error) {
      console.error('Error logging customer activity:', error)
    }
  } catch (error) {
    console.error('Error in logCustomerChange:', error)
  }
}

/**
 * Get activity log for a customer
 */
export async function getCustomerActivity(customerId: string): Promise<ActivityLogEntry[]> {
  const { data, error } = await supabase
    .from('customer_activity_log')
    .select('*')
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching customer activity log:', error)
    return []
  }

  return data || []
}

/**
 * Get human-readable customer action description
 */
export function getCustomerActionDescription(entry: ActivityLogEntry): string {
  const actionDescriptions: Record<string, string> = {
    'created': 'skapade kunden',
    'updated': 'uppdaterade kunden',
    'deleted': 'tog bort kunden',
    'field_changed': 'ändrade',
    'assignment_added': 'lade till uppdrag',
    'assignment_removed': 'tog bort uppdrag'
  }

  if (entry.description) return entry.description

  const baseDescription = actionDescriptions[entry.action_type] || 'gjorde en ändring'

  if (entry.field_name) {
    const fieldNames: Record<string, string> = {
      'name': 'namn',
      'company_name': 'företagsnamn',
      'email': 'e-post',
      'phone': 'telefon',
      'phone_secondary': 'sekundärt telefonnummer',
      'address': 'adress',
      'city': 'stad',
      'postal_code': 'postnummer',
      'location': 'plats',
      'notes': 'anteckningar',
      'internal_notes': 'interna anteckningar',
      'is_active': 'aktivstatus'
    }
    const fieldLabel = fieldNames[entry.field_name] || entry.field_name
    return `${baseDescription} ${fieldLabel}`
  }

  return baseDescription
}

// ============================================
// Subcontractor Activity Log Functions
// ============================================

/**
 * Log a subcontractor change
 */
export async function logSubcontractorChange(
  subcontractorId: string,
  actionType: string,
  options?: {
    fieldName?: string
    oldValue?: string
    newValue?: string
    description?: string
  }
): Promise<void> {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    // Get user profile for name
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('full_name')
      .eq('user_id', user.id)
      .single()

    // Insert the log entry
    const { error } = await supabase
      .from('subcontractor_activity_log')
      .insert({
        subcontractor_id: subcontractorId,
        user_id: user.id,
        user_name: profile?.full_name || user.email || 'Okänd användare',
        action_type: actionType,
        field_name: options?.fieldName,
        old_value: options?.oldValue,
        new_value: options?.newValue,
        description: options?.description
      })

    if (error) {
      console.error('Error logging subcontractor activity:', error)
    }
  } catch (error) {
    console.error('Error in logSubcontractorChange:', error)
  }
}

/**
 * Get activity log for a subcontractor
 */
export async function getSubcontractorActivity(subcontractorId: string): Promise<ActivityLogEntry[]> {
  const { data, error } = await supabase
    .from('subcontractor_activity_log')
    .select('*')
    .eq('subcontractor_id', subcontractorId)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching subcontractor activity log:', error)
    return []
  }

  return data || []
}

/**
 * Get human-readable subcontractor action description
 */
export function getSubcontractorActionDescription(entry: ActivityLogEntry): string {
  const actionDescriptions: Record<string, string> = {
    'created': 'skapade trädgårdsmästaren',
    'updated': 'uppdaterade trädgårdsmästaren',
    'deleted': 'tog bort trädgårdsmästaren',
    'field_changed': 'ändrade',
    'assignment_added': 'lade till uppdrag',
    'assignment_removed': 'tog bort uppdrag'
  }

  if (entry.description) return entry.description

  const baseDescription = actionDescriptions[entry.action_type] || 'gjorde en ändring'

  if (entry.field_name) {
    const fieldNames: Record<string, string> = {
      'name': 'namn',
      'company': 'företag',
      'email': 'e-post',
      'phone': 'telefon',
      'color': 'färg',
      'org_number': 'organisationsnummer',
      'address_street': 'gatuadress',
      'address_city': 'ort',
      'address_postal_code': 'postnummer',
      'address_country': 'land',
      'notes': 'anteckningar',
      'is_active': 'aktivstatus',
      'price_list_id': 'prislista',
      'previous_assignments_count': 'antal tidigare uppdrag'
    }
    const fieldLabel = fieldNames[entry.field_name] || entry.field_name
    return `${baseDescription} ${fieldLabel}`
  }

  return baseDescription
}
